import { ScheduleServiceDialog } from "@/components/site/ScheduleServiceDialog";
import { Link } from "@/i18n/Link";
import { ArrowRight, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/site/PageLayout";
import { Seo, breadcrumbJsonLd, organizationJsonLd } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { SITE_PHONE_DISPLAY } from "@/routes-data";
import { getCities } from "@/locations/cities";
import { useCopy, useLocale } from "@/i18n/LocaleContext";
import { localizedAssetUrl } from "@/i18n/assets";

const COPY = {
  en: {
    home: "Home",
    locations: "Locations",
    seoTitle: "Trailer Axle Service Areas - Northern IN & SW Michigan",
    seoDesc: "Axle Inc. of Elkhart, IN serves 20+ cities across northern Indiana and southwest Michigan with trailer axles, parts, and repair. Find your nearest service area.",
    serviceAreas: "Service Areas",
    heading: "Trailer service across northern Indiana & southwest Michigan.",
    subheading: "From our Elkhart shop, we serve trailer owners, fleets, dealers, and manufacturers across 20 cities - within an hour of our service bay for most customers. Find your community below.",
    schedule: "Schedule service",
    indiana: (count: number) => `Indiana (${count} cities)`,
    michigan: (count: number) => `Michigan (${count} cities)`,
    locationInfo: (dist: any, dir: any, county: any) => `${dist} mi ${dir} of Elkhart · ${county}`,
    viewServices: (name: string) => `View ${name} services`,
    alt: (name: string, state: string) => `${name}, ${state}`
  },
  es: {
    home: "Inicio",
    locations: "Ubicaciones",
    seoTitle: "Áreas de servicio para ejes de remolque - Norte de IN y SO de Michigan",
    seoDesc: "Axle Inc. de Elkhart, IN atiende a más de 20 ciudades en el norte de Indiana y el suroeste de Michigan con ejes de remolque, repuestos y reparaciones. Encuentre su área de servicio más cercana.",
    serviceAreas: "Áreas de servicio",
    heading: "Servicio de remolques en todo el norte de Indiana y el suroeste de Michigan.",
    subheading: "Desde nuestro taller en Elkhart, atendemos a propietarios de remolques, flotas, concesionarios y fabricantes en 20 ciudades, a menos de una hora de nuestro taller para la mayoría de los clientes. Encuentre su comunidad a continuación.",
    schedule: "Programar servicio",
    indiana: (count: number) => `Indiana (${count} ciudades)`,
    michigan: (count: number) => `Michigan (${count} ciudades)`,
    locationInfo: (dist: any, dir: any, county: any) => `${dist} mi al ${dir} de Elkhart · ${county}`,
    viewServices: (name: string) => `Ver servicios en ${name}`,
    alt: (name: string, state: string) => `${name}, ${state}`
  }
};

export default function LocationsIndex() {
  const t = useCopy(COPY);
  const { locale } = useLocale();
  const CITIES = getCities(locale);
  const trail = [
    { name: t.home, path: "/" },
    { name: t.locations, path: "/locations/" },
  ];
  const indianaCities = CITIES.filter((c) => c.state === "IN");
  const michiganCities = CITIES.filter((c) => c.state === "MI");
  return (
    <PageLayout>
      <Seo
        title={t.seoTitle}
        description={t.seoDesc}
        path="/locations/"
        jsonLd={[
          organizationJsonLd(),
          breadcrumbJsonLd(trail),
        ]}
      />

      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="mb-6 [&_a]:text-secondary-foreground/70 [&_a:hover]:text-white [&_span]:text-white">
            <Breadcrumbs trail={trail} />
          </div>
          <p className="text-xs md:text-sm uppercase tracking-[0.22em] text-white font-semibold mb-4">{t.serviceAreas}</p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight max-w-3xl">
            {t.heading}
          </h1>
          <p className="mt-5 text-lg text-secondary-foreground/85 max-w-3xl leading-relaxed">
            {t.subheading}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <ScheduleServiceDialog>
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                {t.schedule} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </ScheduleServiceDialog>
            <Button asChild size="lg" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10">
              <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}>
                <Phone className="mr-1.5 h-4 w-4" /> {SITE_PHONE_DISPLAY}
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-12 md:py-16">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">{t.indiana(indianaCities.length)}</h2>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {indianaCities.map((c) => (
            <Link
              key={c.slug}
              to={`/locations/${c.slug}/`}
              className="group block bg-card border border-border hover:border-primary hover:shadow-md rounded-md overflow-hidden transition-all"
            >
              <div className="aspect-[1200/630] bg-muted overflow-hidden">
                <img src={localizedAssetUrl(`/locations/${c.slug}.svg`, locale)} alt={t.alt(c.name, c.state)} className="w-full h-full object-cover" loading="lazy" width={1200} height={630} />
              </div>
              <div className="p-5">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  {c.name}, {c.state}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">{t.locationInfo(c.distance, c.direction, c.county)}</p>
                <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{c.intro}</p>
                <span className="mt-4 text-sm font-medium text-primary-strong inline-flex items-center">
                  {t.viewServices(c.name)} <ArrowRight className="ml-1 h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="container mx-auto px-4 pb-12 md:pb-16">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">{t.michigan(michiganCities.length)}</h2>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {michiganCities.map((c) => (
            <Link
              key={c.slug}
              to={`/locations/${c.slug}/`}
              className="group block bg-card border border-border hover:border-primary hover:shadow-md rounded-md overflow-hidden transition-all"
            >
              <div className="aspect-[1200/630] bg-muted overflow-hidden">
                <img src={localizedAssetUrl(`/locations/${c.slug}.svg`, locale)} alt={t.alt(c.name, c.state)} className="w-full h-full object-cover" loading="lazy" width={1200} height={630} />
              </div>
              <div className="p-5">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  {c.name}, {c.state}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">{t.locationInfo(c.distance, c.direction, c.county)}</p>
                <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{c.intro}</p>
                <span className="mt-4 text-sm font-medium text-primary-strong inline-flex items-center">
                  {t.viewServices(c.name)} <ArrowRight className="ml-1 h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </PageLayout>
  );
}
